import {jsondiff, stringify, _, computedFn, deepdash} from '@api'
import {globalStores, ui} from '@stores'
import {FLEX_WEIGHT_DEFAULT, FlexComponentName, DEBUG_FLEX_LAYOUT} from '@stores/catalog/flexLayout'
import {guid} from '@utils'
import {action, autorun, computed, observable, runInAction, toJS} from 'mobx'
import {IDisposer} from 'mobx-utils'
import {Action, Actions, adjustSelectedIndexAfterDock, adjustSelectedIndexAfterFloat, Attribute, AttributeDefinitions, BorderNode, BorderSet, DefaultNodeType, DockLocation, DropInfo, FlexLayoutNode, IDraggable, IDropTarget, IJsonBorderNode, IJsonModel, IJsonRowNode, IJsonTabNode, IJsonTabSetNode, ITabSetAttributes, Layout, Orientation, Rect, RowNode, SplitterNode, TabNode, TabSetNode} from '../'

/** @hidden @internal */
export interface ILayoutMetrics {
  headerBarSize: number;
  tabBarSize: number;
  borderBarSize: number;
}

export type FlexLayoutNodesByType = { tab: Array<TabNode>, tabset: Array<TabSetNode>, row: Array<RowNode>, border: Array<BorderNode> }

/**
 * AB = tabset with tabs A and B
 * A | B = row with two tabsets, containing A and B respectively
 * A / B = column with two tabsets, containing A and B respectively
 * S | AB = SidebarRow, tabset with A and B
 *
 * More complex layouts
 * (A | B) / C  =    A   B
 *                   C   C
 *
 * A | (B / C) =     A   A
 *                   B   C
 *
 */
export type FlexLayoutModelShorthand = string

/**
 * Class containing the Tree of Nodes used by the FlexLayout component
 */
export class Model {
  @observable.ref layout: Layout

  replaceFromJson = (jsonString: string) => {
    const json = JSON.parse(jsonString)

    if (json.borders) {
      this._borders = BorderSet._fromJson(json.borders, this)
    }
    this._root = RowNode._fromJson(json.layout, this)
    this.tidy() // initial tidy of node tree
  }

  @observable lastShorthand: string

  registerEffects = () => {
    const {} = this
    this._toDispose.push(
      ui.settings.flexLayout.autoUndo && autorun(() => {
        const {asJson} = this

        runInAction(() => {
          const {changeHistory} = this
          if (changeHistory.length) {
            const last = _.last(this.changeHistory),
              diff = jsondiff.diff(JSON.parse(last), asJson)
            // console.log(`Change detected, saving to undo history`, diff)
          }

          TEST || !changeHistory.length ? this._saveUndoChange(asJson) : this._saveUndoChange_debounced(asJson)
        })
      }))
  }

  @action private _saveUndoChange = (settings: IJsonModel) => {
    const {isInUndo, changeHistory, historyIndex} = this,
      json = stringify(settings)

    if (isInUndo && changeHistory[historyIndex] === json) {} // Do nothing, user just undid/redid
    else {
      if (this.isInUndo) {
        this.changeHistory.replace(this.changeHistory.slice(0, this.historyIndex))
        this.historyIndex = -1
      }

      const {lastShorthand} = this
      DEBUG_FLEX_LAYOUT && console.log(`Layout Changed:  ${lastShorthand} -> ${this.shorthand}`)
      this.lastShorthand = this.shorthand
      this.changeHistory.push(json)
    }
  }

  private _saveUndoChange_debounced = _.debounce((settings: IJsonModel) => {
    this._saveUndoChange(settings)
  }, TEST ? 0 : 250)

  @action dispose = () => {
    this._toDispose.forEach(d => d())
  }

  @observable.ref hoverTab: TabNode

  /**
   * Loads the model from the given json object
   * @param json the json model to load
   * @returns {Model} a new Model object
   */
  static fromJson(json: IJsonModel, isPreview?: boolean, message?: string) {
    DEBUG_FLEX_LAYOUT && message && console.debug(`Deserialize ${isPreview ? 'preview ' : ''}layout from JSON (expensive) - ${message}`, toJS({json}))
    const model = new Model(isPreview === true)
    Model._attributeDefinitions.fromJson(json.global, model._attributes)

    if (json.borders) {
      model._borders = BorderSet._fromJson(json.borders, model)
    }
    model._root = RowNode._fromJson(json.layout, model)
    model.tidy() // initial tidy of node tree

    model._root._children = _.uniqBy(model._root.children, c => c.id)

    if (model._root.children.every(c => !c.attributes.weight)) {
      model._root.children.forEach(v => v._setWeight(FLEX_WEIGHT_DEFAULT))
    }

    model.registerEffects()
    return model
  }

  get flexLayout() { return globalStores.flexLayout}

  @computed get nodesByType(): FlexLayoutNodesByType {
    const {_idMap} = this
    return _.groupBy(_.values(_idMap), n => n.type) as any as FlexLayoutNodesByType
  }

  @computed get nonToolboxTabs() {
    const {nodesByType, flexLayout: {knownTabs}} = this
    return (this.nodesByType.tab ?? []).filter(t => !knownTabs[t.id])
  }

  @computed get firstTabSet(): TabSetNode {
    const {_root, flexLayout: {knownTabs}} = this
    const ts = deepdash.findDeep(_root.children, (v, k, i, ctx) => {
      return v instanceof TabSetNode && v.children.length === 0 || v.children.some(c => !knownTabs[c.id])  // We don't want to insert tabs into a sidebar tabset
    }, {childrenPath: ['_children']})?.value as TabSetNode

    return ts
  }

  jsonForId = computedFn(<T extends BorderNode | RowNode | TabNode | TabSetNode>(id: string) => {
    if (!id) return null
    const {flexLayout} = globalStores
    const model = this.isPreview ? flexLayout.previewModel : flexLayout.model
    if (!model) return null

    const r = model.nodeById(id) as T

    // Node no longer exists - tabs aren't instantly unmounted and may ask for their contents that no longer exist when we're mousing through preview layouts
    return r?.asJson
  })

  /** @hidden @internal */
  private static _attributeDefinitions: AttributeDefinitions = Model._createAttributeDefinitions()

  /** @hidden @internal */
  private static _createAttributeDefinitions(): AttributeDefinitions {
    const attributeDefinitions = new AttributeDefinitions()
    // splitter
    attributeDefinitions.add('splitterSize', -1).setType(Attribute.NUMBER)
    attributeDefinitions.add('enableEdgeDock', true).setType(Attribute.BOOLEAN)
    attributeDefinitions.add('rootOrientationVertical', false).setType(Attribute.BOOLEAN)
    attributeDefinitions.add('marginInsets', {top: 0, right: 0, bottom: 0, left: 0})
      .setType('IInsets')

    // tab
    attributeDefinitions.add('tabEnableClose', true).setType(Attribute.BOOLEAN)
    attributeDefinitions.add('tabCloseType', 1).setType('ICloseType')
    attributeDefinitions.add('tabEnableFloat', false).setType(Attribute.BOOLEAN)
    attributeDefinitions.add('tabEnableDrag', true).setType(Attribute.BOOLEAN)
    attributeDefinitions.add('tabEnableRename', true).setType(Attribute.BOOLEAN)
    attributeDefinitions.add('tabClassName', undefined).setType(Attribute.STRING)
    attributeDefinitions.add('tabIcon', undefined).setType(Attribute.STRING)
    attributeDefinitions.add('tabEnableRenderOnDemand', true).setType(Attribute.BOOLEAN)
    attributeDefinitions.add('tabDragSpeed', 0.3).setType(Attribute.NUMBER)

    // tabset
    attributeDefinitions.add('tabSetEnableDeleteWhenEmpty', true).setType(Attribute.BOOLEAN)
    attributeDefinitions.add('tabSetEnableDrop', true).setType(Attribute.BOOLEAN)
    attributeDefinitions.add('tabSetEnableDrag', true).setType(Attribute.BOOLEAN)
    attributeDefinitions.add('tabSetEnableDivide', true).setType(Attribute.BOOLEAN)
    attributeDefinitions.add('tabSetEnableMaximize', true).setType(Attribute.BOOLEAN)
    attributeDefinitions.add('tabSetAutoSelectTab', true).setType(Attribute.BOOLEAN)
    attributeDefinitions.add('tabSetClassNameTabStrip', undefined).setType(Attribute.STRING)
    attributeDefinitions.add('tabSetClassNameHeader', undefined).setType(Attribute.STRING)
    attributeDefinitions.add('tabSetEnableTabStrip', true).setType(Attribute.BOOLEAN)
    attributeDefinitions.add('tabSetHeaderHeight', 0).setType(Attribute.NUMBER)
    attributeDefinitions.add('tabSetTabStripHeight', 0).setType(Attribute.NUMBER)
    attributeDefinitions.add('tabSetMarginInsets', {top: 0, right: 0, bottom: 0, left: 0})
      .setType('IInsets')
    attributeDefinitions.add('tabSetBorderInsets', {top: 0, right: 0, bottom: 0, left: 0})
      .setType('IInsets')
    attributeDefinitions.add('tabSetTabLocation', 'top').setType('ITabLocation')
    attributeDefinitions.add('tabSetMinWidth', 0).setType(Attribute.NUMBER)
    attributeDefinitions.add('tabSetMinHeight', 0).setType(Attribute.NUMBER)

    // border
    attributeDefinitions.add('borderSize', 200).setType(Attribute.NUMBER)
    attributeDefinitions.add('borderMinSize', 0).setType(Attribute.NUMBER)
    attributeDefinitions.add('borderBarSize', 0).setType(Attribute.NUMBER)
    attributeDefinitions.add('borderEnableDrop', true).setType(Attribute.BOOLEAN)
    attributeDefinitions.add('borderAutoSelectTabWhenOpen', true).setType(Attribute.BOOLEAN)
    attributeDefinitions.add('borderAutoSelectTabWhenClosed', false).setType(Attribute.BOOLEAN)
    attributeDefinitions.add('borderClassName', undefined).setType(Attribute.STRING)
    return attributeDefinitions
  }

  /** @hidden @internal */
  @observable _attributes: Record<string, any>
  /** @hidden @internal */
  private _nextId: number
  /** @hidden @internal */
  _changeListener?: (action ?: Action) => void
  /** @hidden @internal */
  @observable private _root?: RowNode
  /** @hidden @internal */
  @observable private _borders: BorderSet
  /** @hidden @internal */
  private _onAllowDrop?: (dragNode: FlexLayoutNode, dropInfo: DropInfo) => boolean
  /** @hidden @internal */
  @observable private _maximizedTabSet?: TabSetNode
  /** @hidden @internal */
  @observable private _activeTabSet?: TabSetNode
  /** @hidden @internal */
  @observable private _borderRects: { inner: Rect; outer: Rect } = {inner: Rect.empty(), outer: Rect.empty()}
  /** @hidden @internal */
  private _pointerFine: boolean
  /** @hidden @internal */
  private _onCreateTabSet?: (tabNode?: TabNode) => ITabSetAttributes

  /**
   * 'private' constructor. Use the static method Model.fromJson(json) to create a model
   *  @hidden @internal
   */

  private constructor(public isPreview = false) {
    this._attributes = {}
    this._nextId = 0
    this._borders = new BorderSet(this)
    this._pointerFine = true
  }

  _toDispose: IDisposer[] = []

  /** @hidden @internal */
  _setChangeListener(listener: (() => void) | undefined) {
    this._changeListener = listener
  }

  /**
   * Get the currently active tabset node
   */
  @computed get activeTabset() {
    if (this._activeTabSet && this.nodeById(this._activeTabSet.id)) {
      return this._activeTabSet
    } else {

      return undefined
    }
  }

  /** @hidden @internal */
  @action _setActiveTabset = (tabsetNode: TabSetNode | undefined) => {
    this._activeTabSet = tabsetNode
  }

  /**
   * Get the currently maximized tabset node
   */
  get maximizedTabset() {
    return this._maximizedTabSet
  }

  /** @hidden @internal */
  @action _setMaximizedTabset = (tabsetNode: (TabSetNode | undefined)) => {
    this._maximizedTabSet = tabsetNode
  }

  /**
   * Gets the root RowNode of the model
   * @returns {RowNode}
   */
  get root(): RowNode {
    return this._root as RowNode
  }

  get isRootOrientationVertical() {
    return this._attributes.rootOrientationVertical as boolean
  }

  /**
   * Gets the
   * @returns {BorderSet|*}
   */
  get borderSet() {
    return this._borders
  }

  /** @hidden @internal */
  get outerInnerRects() {
    return this._borderRects
  }

  /** @hidden @internal */
  _getPointerFine = () => {
    return this._pointerFine
  }

  /** @hidden @internal */
  @action _setPointerFine = (pointerFine: boolean) => {
    this._pointerFine = pointerFine
  }

  /**
   * Visits all the nodes in the model and calls the given function for each
   * @param fn a function that takes visited node and a integer level as parameters
   */
  visitNodes = (fn: (node: FlexLayoutNode, level: number) => void) => {
    this._borders._forEachNode(fn);
    (this._root as RowNode)._forEachNode(fn, 0)
  }

  /** @hidden @internal */
  //@observable _idMap: Record<string, FlexLayoutNode>

  @computed get _idMap(): Record<string, FlexLayoutNode> {
    let map = {}

    const {_borders, _root} = this,
      fn = (node, level) => map[node.id] = node

    _borders._forEachNode(fn)
    _root && (_root as RowNode)._forEachNode(fn, 0)

    return map
  }

  get _idMap2(): Record<string, FlexLayoutNode> {
    let map = {}

    const {_borders, _root} = this,
      fn = (node, level) => map[node.id] = node

    _borders._forEachNode(fn)
    _root && (_root as RowNode)._forEachNode(fn, 0)

    return map
  }

  /**
   * Gets a node by its id
   * @param id the id to find
   */
  nodeById = <T extends FlexLayoutNode>(id: FlexComponentName): T => {
    return this._idMap[id] as T
  }

  @observable lastAction: string = 'load'

  /**
   * Update the node tree by performing the given action,
   * Actions should be generated via static methods on the Actions class
   * @param action the action to perform
   * @returns added Node for Actions.addNode; undefined otherwise
   */
  @action doAction = (action: Action): FlexLayoutNode | undefined => {
    let returnVal = undefined
    this.lastAction = action.type
    // console.log(action);
    switch (action.type) {
      case Actions.ADD_NODE: {
        const newNode = new TabNode(this, action.data.json, true)
        const toNode = this._idMap[action.data.toNode] as FlexLayoutNode & IDraggable
        if (toNode instanceof TabSetNode || toNode instanceof BorderNode || toNode instanceof RowNode) {
          toNode.drop(newNode, DockLocation.getByName(action.data.location), action.data.index, action.data.select)
          returnVal = newNode
        }
        break
      }
      case Actions.MOVE_NODE: {
        const fromNode = this._idMap[action.data.fromNode] as FlexLayoutNode & IDraggable
        if (fromNode instanceof TabNode || fromNode instanceof TabSetNode) {
          const toNode = this._idMap[action.data.toNode] as FlexLayoutNode & IDropTarget
          if (toNode instanceof TabSetNode || toNode instanceof BorderNode || toNode instanceof RowNode) {
            toNode.drop(fromNode, DockLocation.getByName(action.data.location), action.data.index, action.data.select)
          }
        }
        break
      }
      case Actions.DELETE_TAB: {
        const node = this._idMap[action.data.node]

        if (node instanceof TabNode) {
          node._delete()
        }
        break
      }
      case Actions.FLOAT_TAB: {
        const node = this._idMap[action.data.node]
        if (node instanceof TabNode) {
          node._setFloating(true)
          adjustSelectedIndexAfterFloat(node)
        }
        break
      }
      case Actions.UNFLOAT_TAB: {
        const node = this._idMap[action.data.node]
        if (node instanceof TabNode) {
          node._setFloating(false)
          adjustSelectedIndexAfterDock(node)
        }
        break
      }
      case Actions.RENAME_TAB: {
        const node = this._idMap[action.data.node]
        if (node instanceof TabNode) {
          node._setName(action.data.text)
        }
        break
      }
      case Actions.SELECT_TAB: {
        const tabNode = this._idMap[action.data.tabNode]
        if (tabNode instanceof TabNode) {
          const parent = tabNode.parent as FlexLayoutNode,
            pos = tabNode.indexInParent

          if (parent instanceof BorderNode) {
            if (parent.selected === pos) {
              parent._setSelected(-1)
            } else {
              parent._setSelected(pos)
            }
          } else if (parent instanceof TabSetNode) {
            if (parent.selected !== pos) {
              parent._setSelected(pos)
            }
            this._activeTabSet = parent
          }
        }
        break
      }
      case Actions.SET_ACTIVE_TABSET: {
        const tabsetNode = this._idMap[action.data.tabsetNode]
        if (tabsetNode instanceof TabSetNode) {
          this._activeTabSet = tabsetNode
        }
        break
      }
      case Actions.ADJUST_SPLIT: {
        const node1 = this._idMap[action.data.node1]
        const node2 = this._idMap[action.data.node2]

        if ((node1 instanceof TabSetNode || node1 instanceof RowNode) && (node2 instanceof TabSetNode || node2 instanceof RowNode)) {
          this._adjustSplitSide(node1, action.data.weight1, action.data.pixelWidth1)
          this._adjustSplitSide(node2, action.data.weight2, action.data.pixelWidth2)
        }
        break
      }
      case Actions.ADJUST_BORDER_SPLIT: {
        const node = this._idMap[action.data.node]
        if (node instanceof BorderNode) {
          node._setSize(action.data.pos)
        }
        break
      }
      case Actions.MAXIMIZE_TOGGLE: {
        const node = this._idMap[action.data.node]
        if (node instanceof TabSetNode) {
          if (node === this._maximizedTabSet) {
            this._maximizedTabSet = undefined
          } else {
            this._maximizedTabSet = node
            this._activeTabSet = node
          }
        }

        break
      }
      case Actions.UPDATE_MODEL_ATTRIBUTES: {
        this._updateAttrs(action.data.json)
        break
      }

      case Actions.UPDATE_NODE_ATTRIBUTES: {
        const node = this._idMap[action.data.node]
        node._updateAttrs(action.data.json)
        break
      }
      default:
        break
    }

    if (this._changeListener !== undefined) {
      this._changeListener(action)
    }

    return returnVal
  }

  /** @hidden @internal */
  _adjustSplitSide(node: TabSetNode | RowNode, weight: number, pixels: number) {
    // console.log(`_adjustSplitSide`, {weight, pixels})
    node._setWeight(weight)
    if (node.width != null && node.orientation === Orientation.VERT) {
      node._updateAttrs({width: pixels})
    } else if (node.height != null && node.orientation === Orientation.HORZ) {
      node._updateAttrs({height: pixels})
    }
  }

  /**
   * Converts the model to a json object
   * @returns {IJsonModel} json object that represents this model
   */
  @computed get asJson(): IJsonModel {
    const json: any = {global: {}, layout: {}}
    Model._attributeDefinitions.toJson(json.global, this._attributes)

    // save state of nodes
    this.visitNodes((node) => {
      node._fireEvent('save', undefined)
    })

    delete json.global
    json.borders = this._borders.asJson
    json.layout = (this._root as RowNode).asJson
    return json as IJsonModel
  }

  @computed get asJsonStringified() {
    return stringify(this.asJson)
  }

  @computed get splitterSize() {
    let splitterSize = this._attributes.splitterSize as number
    if (splitterSize === -1) {
      // use defaults
      splitterSize = this._pointerFine ? 8 : 12 // larger for mobile
    }
    return splitterSize
  }

  isEnableEdgeDock = () => {
    return this._attributes.enableEdgeDock as boolean
  }

  /** @hidden @internal */
  _addNode = (node: FlexLayoutNode) => {
    const id = node.id
    if (this._idMap[id] !== undefined) {
      console.warn(`Duplicate ID ${id} in layout model, ignoring node`, node)
      //node.attributes.id = guid()
      //throw new Error(`Error: each node must have a unique id, duplicate id:${node.id}`);
    }

    this._toDispose.push(...node.registerEffects())
  }

  /** @hidden @internal */
  @action _layout = (rect: Rect, metrics: ILayoutMetrics) => {
    // let start = Date.now();
    this._borderRects = this._borders._layoutBorder({outer: rect, inner: rect}, metrics)
    rect = this._borderRects.inner.removeInsets(this._getAttribute('marginInsets'))

    this._root?.calcMinSize();
    (this._root as RowNode)._layout(rect, metrics)
    // console.log("layout time: " + (Date.now() - start));
    return rect
  }

  /** @hidden @internal */
  @action _findDropTargetNode = (dragNode: FlexLayoutNode & IDraggable, x: number, y: number) => {
    let node = (this._root as RowNode)._findDropTargetNode(dragNode, x, y)
    if (node === undefined) {
      node = this._borders._findDropTargetNode(dragNode, x, y)
    }
    return node
  }

  /** @hidden @internal */
  @action tidy = () => {
    // console.log("before _tidy", this.toString());
    (this._root as RowNode)._tidy()
    // console.log("after _tidy", this.toString());
  }

  /** @hidden @internal */
  _updateAttrs = (json: any) => {
    Model._attributeDefinitions.update(json, this._attributes)
  }

  /** @hidden @internal */
  _nextUniqueId = () => {
    this._nextId++
    while (this._idMap['#' + this._nextId] !== undefined) {
      this._nextId++
    }

    return guid()
  }

  /** @hidden @internal */
  _getAttribute = (name: string): any => {
    return this._attributes[name]
  }

  /**
   * Sets a function to allow/deny dropping a node
   * @param onAllowDrop function that takes the drag node and DropInfo and returns true if the drop is allowed
   */
  setOnAllowDrop = (onAllowDrop: (dragNode: FlexLayoutNode, dropInfo: DropInfo) => boolean) => {
    this._onAllowDrop = onAllowDrop
  }

  /** @hidden @internal */
  _getOnAllowDrop = () => {
    return this._onAllowDrop
  }

  /**
   * set callback called when a new TabSet is created.
   * The tabNode can be undefined if it's the auto created first tabset in the root row (when the last
   * tab is deleted, the root tabset can be recreated)
   * @param onCreateTabSet
   */
  setOnCreateTabSet = (onCreateTabSet: (tabNode?: TabNode) => ITabSetAttributes) => {
    this._onCreateTabSet = onCreateTabSet
  }

  /** @hidden @internal */
  _getOnCreateTabSet = () => {
    return this._onCreateTabSet
  }

  static toTypescriptInterfaces() {
    console.log(Model._attributeDefinitions.toTypescriptInterface('Global', undefined))
    console.log(RowNode.getAttributeDefinitions().toTypescriptInterface('Row', Model._attributeDefinitions))
    console.log(TabSetNode.getAttributeDefinitions().toTypescriptInterface('TabSet', Model._attributeDefinitions))
    console.log(TabNode.getAttributeDefinitions().toTypescriptInterface('Tab', Model._attributeDefinitions))
    console.log(BorderNode.getAttributeDefinitions().toTypescriptInterface('Border', Model._attributeDefinitions))
  }

  @computed get asString() {
    return JSON.stringify(this.asJson)
  }

  @action updateAndLayout = () => {
    this._changeListener && this._changeListener()
    this.layout.store.doLayout()
  }

  @action onChange = (action?: Action) => {
    const {_changeListener} = this
    _changeListener && _changeListener(action)
  }

  /**
   * An easy to understand shorthand to describe some complex flex layout
   * aB = tabset with id:a, id:b and b is selected
   * aB | cD = two tabsets in a column layout
   * A / B = two tabsets in a column
   *
   */
  @computed get shorthand() {
    return this.root.shorthand
  }

  changeHistory = observable.array<string>([], {deep: false})
  @observable historyIndex = -1

  @computed get canUndo() {
    if (!ui.settings.flexLayout.autoUndo) return false
    const {changeHistory, historyIndex} = this
    return !_.isEmpty(changeHistory) && (historyIndex === -1 || historyIndex > 0)
  }

  @computed get canRedo() {
    if (!ui.settings.flexLayout.autoUndo) return false
    const {changeHistory, historyIndex} = this
    return !_.isEmpty(changeHistory) && historyIndex !== -1 && historyIndex < changeHistory.length
  }

  @action undo = () => {
    if (!ui.settings.flexLayout.autoUndo) return

    const {changeHistory, historyIndex} = this
    this.historyIndex = Math.max(0, historyIndex === -1 ? changeHistory.length - 2 : historyIndex - 1)

    const {settingsForHistoryIndex} = this
    this.replaceFromJson(settingsForHistoryIndex)
  }

  @action redo = () => {
    if (!ui.settings.flexLayout.autoUndo) return

    const {changeHistory, historyIndex} = this
    this.historyIndex = (historyIndex + 1 === changeHistory.length) ? -1 : historyIndex + 1

    const {settingsForHistoryIndex} = this
    this.replaceFromJson(settingsForHistoryIndex)
  }

  @computed get isInUndo() { return this.historyIndex !== -1}

  @computed get settingsForHistoryIndex() : string {
    const {historyIndex, changeHistory} = this
    return historyIndex === -1 ? stringify(this.asJson) : changeHistory[historyIndex]
  }
}



// WEBPACK FOOTER //
// ./src/stores/catalog/flexLayout/flexlayout-react/src/model/Model.ts